import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react'
import { Mutex } from 'async-mutex'
import Cookies from 'universal-cookie'
import { RootState } from './store'
import { clearToken, setToken } from './authSlice'
import qs from 'qs'
import { isObject } from 'lodash'

const CollectionTag = 'Collection'
const UserTag = 'User'
const ProfileTag = 'Profile'
const SubscriptionTag = 'Subscription'
const CollectionRequestTag = 'CollectionRequest'
const ACLTag = 'ACLTag'

// create a new mutex
const mutex = new Mutex()
const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URI,
  prepareHeaders: (headers, api) => {
    let token = (api.getState() as RootState).auth.token
    if (!token) {
      token = localStorage.getItem('echoesToken') || undefined
    }
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
      const cookies = new Cookies()
      const cookieToken = cookies.get('token')
      if (cookieToken !== token) {
        cookies.set('token', token)
      }
    } else {
      console.debug('No token in redux')
    }
    return headers
  },
  paramsSerializer: (params) => {
    return qs.stringify(params)
  },
  //credentials: 'include'
})
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  {},
  { response?: { headers: { get: (header: string) => string | null } } }
> = async (args, api, extraOptions) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock()
  let result = await baseQuery(args, api, extraOptions)
  if (result.error && result.error.status === 401) {
    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire()
      try {
        const refreshResult = await baseQuery(
          '/refreshToken',
          api,
          extraOptions
        )
        if (refreshResult.data) {
          api.dispatch(setToken(refreshResult.data))
          // retry the initial query
          result = await baseQuery(args, api, extraOptions)
        } else {
          api.dispatch(clearToken())
        }
      } finally {
        // release must be called once the mutex should be released again.
        release()
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock()
      result = await baseQuery(args, api, extraOptions)
    }
  }
  return result
}

export const api = createApi({
  reducerPath: 'echoes',
  tagTypes: [
    CollectionTag,
    ProfileTag,
    UserTag,
    SubscriptionTag,
    CollectionRequestTag,
    ACLTag
  ],
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    // collections
    getAllCollections: build.query<
      { data: ICollectionModel[]; count: number },
      IWalkQueryParams
    >({
      query: (params) => ({
        url: '/api/v2/collections',
        params,
      }),
      transformResponse: (res: ICollectionModel[], meta) => ({
        data: res,
        count: Number(meta?.response?.headers.get('X-Total-Count')),
      }),
      providesTags: (result) =>
        result
          ? result.data.map(({ _id }) => ({ type: CollectionTag, id: _id }))
          : [],
    }),
    getCollections: build.infiniteQuery<
      { data: ICollectionModel[]; count: number },
      // { data: ICollectionModel[]; count: number },
      IWalkQueryParams,
      { skip: number; limit: number }
    >({
      infiniteQueryOptions: {
        // Must provide a default initial page param value
        initialPageParam: { skip: 0, limit: 10 },
        // Optionally limit the number of cached pages
        maxPages: 3,
        // Must provide a `getNextPageParam` function
        getNextPageParam: (
          lastPage,
          allPages,
          lastPageParam,
          allPageParams
        ) => {
          const nextPageSkip =
            (lastPageParam.skip ?? 0) + (lastPageParam.limit ?? 10)
          const morePagesExist = nextPageSkip < lastPage.count
          if (!morePagesExist) return undefined
          return {
            ...lastPageParam,
            skip: nextPageSkip,
            limit: lastPageParam.limit ?? 10,
          }
        },
        getPreviousPageParam: (
          lastPage,
          allPages,
          firstPageParam,
          allPageParams
        ) => {
          const prevOffset =
            (firstPageParam.skip ?? 0) - (firstPageParam.limit ?? 10)
          if (prevOffset < 0) return undefined

          return {
            ...firstPageParam,
            skip: (firstPageParam.skip ?? 0) - (firstPageParam.limit ?? 10),
          }
        },
      },
      transformResponse: (res: ICollectionModel[], meta) => ({
        data: res,
        count: Number(meta?.response?.headers.get('X-Total-Count')),
      }),
      query: ({ queryArg, pageParam }) => {
        return {
          url: '/api/v2/collections',
          params: { ...queryArg, ...pageParam },
        }
      },
    }),
    getMyCollections: build.query<
      { data: ICollectionModel[]; count: number },
      IWalkQueryParams
    >({
      query: (params) => ({
        url: '/api/v2/collections/mine',
        params,
      }),
      transformResponse: (res: ICollectionModel[], meta) => ({
        data: res,
        count: Number(meta?.response?.headers.get('X-Total-Count')),
      }),
      providesTags: (result) =>
        result
          ? result.data.map(({ _id }) => ({ type: CollectionTag, id: _id }))
          : [],
    }),
    getCollectionsForProfile: build.query<
      { data: ICollectionModel[]; count: number },
      string
    >({
      query: (profile) => ({
        url: '/api/v2/collections/',
        params: {
          profiles: [profile],
        },
      }),
      transformResponse: (res: ICollectionModel[], meta) => {
        return {
          data: res,
          count: Number(meta?.response?.headers.get('X-Total-Count')),
        }
      },
      providesTags: (result) =>
        result
          ? result.data.map(({ _id }) => ({ type: CollectionTag, id: _id }))
          : [],
    }),
    getCollection: build.query<ICollectionModel, string>({
      query: (slug) => ({
        url: `/api/v2/collections/${slug}`,
        params: {
          populate: true,
        },
      }),
      providesTags: (result) =>
        result ? [{ type: CollectionTag, id: result._id }] : [],
    }),
    getCollectionChannels: build.query<string[], string | undefined>({
      query: () => '/api/v2/collections/channels',
    }),
    getCollectionTags: build.query<string[], string | undefined>({
      query: () => '/api/v2/collections/tags',
    }),
    duplicateCollection: build.mutation<ICollectionModel, string>({
      query: (slug) => ({
        url: `/api/v2/collections/${slug}/duplicate`,
        method: 'POST',
      }),
      invalidatesTags: [CollectionTag],
    }),
    createCollection: build.mutation<
      ICollectionModel,
      Partial<ICollectionModel>
    >({
      query: (body) => ({
        url: '/api/v2/collections',
        method: 'POST',
        body,
      }),
      invalidatesTags: [CollectionTag],
    }),
    updateCollection: build.mutation<
      ICollectionModel,
      Partial<ICollectionModel>
    >({
      query: (body) => ({
        url: `/api/v2/collections/${body.slug}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [CollectionTag],
    }),
    deleteCollection: build.mutation<void, string>({
      query: (slug) => ({
        url: `/api/v2/collections/${slug}`,
        method: 'DELETE',
      }),
      invalidatesTags: [CollectionTag],
    }),
    getCollectionAcl: build.query<IACL[], string>({
      query: (slug) => ({
        url: `/api/v2/collections/${slug}/acl`,
        method: 'GET'
      }),
      providesTags: (result) =>
        result
          ? result.map(({ user }) => ({ type: ACLTag, id: isObject(user) ? user._id : user }))
          : [],
    }),
    setCollectionAcl: build.mutation<void, ICollectionACLRequest>({
      query: ({ slug, acl }) => ({
        url: `/api/v2/collections/${slug}/acl`,
        method: 'POST',
        body: acl
      }),
      invalidatesTags: [ACLTag],
    }),
    // profiles
    getAllProfiles: build.query<
      { data: IProfileModel[]; count: number },
      IProfileParams
    >({
      query: (params) => ({
        url: '/api/v2/profiles',
        method: 'GET',
        params,
      }),
      transformResponse: (res: IProfileModel[], meta) => {
        return {
          data: res,
          count: Number(meta?.response?.headers.get('X-Total-Count')),
        }
      },
      providesTags: (result) =>
        result
          ? result.data.map(({ _id }) => ({ type: ProfileTag, id: _id }))
          : [],
    }),
    getAllProfilesInfinite: build.infiniteQuery<
      IProfilesResponse,
      IProfileParams,
      ISkipLimitParams
    >({
      infiniteQueryOptions: {
        initialPageParam: {
          skip: 0,
          limit: 10,
        },
        getNextPageParam: (lastPage, allPages, lastPageParam, allPageParam) => {
          const nextPageSkip =
            (lastPageParam.skip ?? 0) + (lastPageParam.limit ?? 10)
          const morePagesExist = nextPageSkip < lastPage.count
          if (!morePagesExist) return undefined
          return {
            ...lastPageParam,
            skip: nextPageSkip,
            limit: lastPageParam.limit ?? 10,
          }
        },
        getPreviousPageParam: (
          firstPage,
          allPages,
          firstPageParam,
          allPageParams
        ) => {
          const prevOffset =
            (firstPageParam.skip ?? 0) - (firstPageParam.limit ?? 10)
          if (prevOffset < 0) return undefined

          return {
            ...firstPageParam,
            skip: (firstPageParam.skip ?? 0) - (firstPageParam.limit ?? 10),
          }
        },
      },
      query: ({ pageParam, queryArg }) => ({
        url: '/api/v2/profiles',
        method: 'GET',
        params: { ...pageParam, ...queryArg },
      }),
      providesTags: (result) =>
        result
          ? result.pages.flatMap((page) =>
            page.data.map(({ _id }) => ({ type: ProfileTag, id: _id }))
          )
          : [],
      transformResponse: (response, meta) => {
        const count = Number(meta?.response?.headers.get('X-Total-Count'))
        return { data: response as IProfileModel[], count }
      },
    }),
    getMyProfiles: build.query<
      { data: IProfileModel[]; count: number },
      IProfileParams
    >({
      query: (params) => ({
        params,
        url: '/api/v2/profiles/mine',
      }),
      providesTags: (result) =>
        result
          ? result.data.map(({ _id }) => ({ type: ProfileTag, id: _id }))
          : [],
      transformResponse: (response, meta) => {
        const count = Number(meta?.response?.headers.get('X-Total-Count'))
        return { data: response as IProfileModel[], count }
      },
    }),
    getProfile: build.query<IProfileModel, string>({
      query: (slug) => `/api/v2/profiles/${slug}`,
    }),
    createProfile: build.mutation<IProfileModel, IProfileModel>({
      query: (profile) => ({
        url: '/api/v2/profiles',
        method: 'POST',
        body: profile,
      }),
      invalidatesTags: [ProfileTag],
    }),
    updateProfile: build.mutation<IProfileModel, IProfileModel>({
      query: (profile) => ({
        url: `/api/v2/profiles/${profile.slug}`,
        method: 'PUT',
        body: profile,
      }),
      invalidatesTags: [ProfileTag],
    }),
    deleteProfile: build.mutation<IProfileModel, string>({
      query: (slug) => ({
        url: `/api/v2/profiles/${slug}`,
        method: 'DELETE',
      }),
      invalidatesTags: [ProfileTag],
    }),
    // subscriptions
    getAllSubscriptions: build.query<
      TSubscriptionProfileModel[],
      ISubscriptionParams
    >({
      query: () => '/api/v2/subscriptions',
      providesTags: (result) =>
        result
          ? result.map(({ _id }) => ({ type: SubscriptionTag, id: _id }))
          : [],
    }),
    getMySubscriptions: build.query<
      TSubscriptionProfileModel[],
      ISubscriptionParams
    >({
      query: () => '/api/v2/subscriptions/mine',
      providesTags: (result) =>
        result
          ? result.map(({ _id }) => ({ type: SubscriptionTag, id: _id }))
          : [],
    }),
    getSubscription: build.query<TSubscriptionProfileModel, string>({
      query: (id) => `/api/v2/profiles/${id}`,
      providesTags: (result) =>
        result ? [{ type: SubscriptionTag, id: result._id }] : [],
    }),
    createSubscription: build.mutation<
      ISubscriptionModel,
      ISubscriptionPaymentModel
    >({
      query: (body) => ({
        url: '/api/v2/subscriptions/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [SubscriptionTag],
    }),
    updateSubscription: build.mutation<
      ISubscriptionModel,
      TSubscriptionProfileModel
    >({
      query: (body) => ({
        url: `api/v2/subscriptions/${body._id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [SubscriptionTag],
    }),
    deleteSubscription: build.mutation<ISubscriptionModel, string>({
      query: (_id) => ({
        url: `api/v2/subscriptions/${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [SubscriptionTag],
    }),
    getSubscriptionAcl: build.query<IACL[], string>({
      query: (_id) => `/api/v2/subscriptions/${_id}/acl`
    }),
    setSubscriptionAcl: build.mutation<void, ISubscriptionACLRequest>({
      query: ({ id, acl }) => ({
        url: `/api/v2/subscriptions/${id}/acl`,
        method: 'PUT',
        body: acl
      }),
    }),
    goToStripePortal: build.mutation<{ url: string }, IGoToStripePortalData>({
      query: (body) => ({
        url: '/api/v2/subscriptions/create-customer-portal-session',
        method: 'POST',
        body,
      }),
    }),
    createStripeCheckoutSession: build.mutation<
      IStripeCustomerCheckoutSessionResponse,
      IStripeCustomerCheckoutSessionData
    >({
      query: (body) => ({
        url: '/api/v2/subscriptions/create-checkout-session',
        method: 'POST',
        body,
      }),
    }),
    getSubscriptionProducts: build.query<IProductModel[], IProductParams>({
      query: (params) => ({
        url: '/api/v2/subscriptions/products',
        params,
      }),
    }),
    // user
    getAllUsers: build.query<
      { data: IUserModel[]; count: number },
      IUsersAdminParams
    >({
      query: (params) => ({
        url: '/api/v2/users',
        params,
      }),
      transformResponse: (res: IUserModel[], meta) => {
        return {
          data: res,
          count: Number(meta?.response?.headers.get('X-Total-Count')),
        }
      },
      providesTags: (result) =>
        result
          ? result.data.map(({ _id }) => ({ type: ProfileTag, id: _id }))
          : [],
    }),
    getUser: build.query<IUserModel, string>({
      query: (slug) => `/api/v2/users/${slug}?populate=true`,
      providesTags: (result) =>
        result && result._id ? [{ type: UserTag, id: result._id }] : [],
    }),
    updateUser: build.mutation<IUserModel, Partial<IUserModel>>({
      query: (body) => ({
        url: `/api/v2/users/${body.slug}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [UserTag],
    }),
    // appConfig
    getAppConfig: build.query<IAppConfig, null>({
      query: () => '/api/v2/appconfig/creator.echoes.xyz',
    }),
    // media
    getImageProxySizes: build.query<IImageProxySizesResponse, IMedium>({
      query: (body) => ({
        url: '/api/v2/media/image/sizes',
        method: 'POST',
        body,
      }),
    }),
    uploadToSignedUrl: build.query<IFileUploadResponse, string>({
      query: (filename) =>
        `/api/v2/media/signed-upload-url?filename=${filename}`,
    }),
    // stats
    getStatsForCollection: build.query<
      ITrendStatsResponse,
      ISingleWalkStatQuery
    >({
      query: (params) => {
        return {
          url: `/api/v2/stats/collection/${params.slug}/`,
          params,
        }
      },
    }),
    updateUserProfile: build.mutation<IUserModel, Partial<IUserModel>>({
      query: (body) => ({
        url: '/api/v2/users/me',
        method: 'PUT',
        body,
      }),
    }),
    getCollectionRequests: build.query<
      ICollectionRequest[],
      ICollectionRequestQueryParams
    >({
      query: (params) => ({
        url: '/api/v2/collections/requests',
        method: 'GET',
        params,
      }),
      providesTags: (result) =>
        result
          ? result.map(({ _id }) => ({ type: CollectionRequestTag, id: _id }))
          : [],
    }),
    getCollectionRequest: build.query<ICollectionRequest, string>({
      query: (id) => ({
        url: `/api/v2/collections/requests/${id}`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result ? [{ type: CollectionRequestTag, id: result._id }] : [],
    }),
    getMyCollectionRequests: build.query<
      ICollectionRequest[],
      ICollectionRequestQueryParams
    >({
      query: (params) => ({
        url: '/api/v2/collections/requests/mine',
        method: 'GET',
        params,
      }),
      providesTags: (result) =>
        result
          ? result.map(({ _id }) => ({ type: CollectionRequestTag, id: _id }))
          : [],
    }),
    createCollectionRequest: build.mutation<
      ICollectionRequest,
      ICreateCollectionRequest
    >({
      query: (body) => ({
        url: '/api/v2/collections/requests',
        method: 'POST',
        body,
      }),
      invalidatesTags: [CollectionRequestTag],
    }),
    updateCollectionRequestStatus: build.mutation<
      ICollectionRequest,
      Pick<ICollectionRequest, 'status' | 'admin_notes'> & { _id: string }
    >({
      query: (body) => ({
        url: `/api/v2/collections/requests/${body._id}/status`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [CollectionRequestTag],
    }),
  }),
})

export const {
  useGetAllCollectionsQuery,
  // named like this because of naming conflict
  useGetCollectionsInfiniteQuery: useGetAllCollectionsInfiniteQuery,
  useGetMyCollectionsQuery,
  useGetCollectionsForProfileQuery,
  useGetCollectionQuery,
  useGetCollectionChannelsQuery,
  useGetCollectionTagsQuery,
  useDuplicateCollectionMutation,
  useCreateCollectionMutation,
  useUpdateCollectionMutation,
  useDeleteCollectionMutation,
  useGetCollectionAclQuery,
  useSetCollectionAclMutation,
  useGetAllProfilesQuery,
  useGetAllProfilesInfiniteInfiniteQuery,
  useGetMyProfilesQuery,
  useGetProfileQuery,
  useCreateProfileMutation,
  useUpdateProfileMutation,
  useDeleteProfileMutation,
  useGetAllSubscriptionsQuery,
  useGetMySubscriptionsQuery,
  useGetSubscriptionQuery,
  useCreateSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useDeleteSubscriptionMutation,
  useGetSubscriptionAclQuery,
  useSetSubscriptionAclMutation,
  useCreateStripeCheckoutSessionMutation,
  useGoToStripePortalMutation,
  useGetSubscriptionProductsQuery,
  useGetAllUsersQuery,
  useGetUserQuery,
  useUpdateUserMutation,
  useGetAppConfigQuery,
  useGetImageProxySizesQuery,
  useUploadToSignedUrlQuery,
  useGetStatsForCollectionQuery,
  useUpdateUserProfileMutation,
  useGetCollectionRequestsQuery,
  useGetCollectionRequestQuery,
  useGetMyCollectionRequestsQuery,
  useCreateCollectionRequestMutation,
  useUpdateCollectionRequestStatusMutation,
  util,
} = api
